import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import toaster from 'react-hot-toast';
import { getTurnServiceTypes, getAllTeamMembersForBusiness, getAllCustomersForBusiness } from 'api/admin/devices/devices';

// components and styling
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import styles from 'components/admin/deviceDashboard/deviceEditor/DeviceSimulator.module.scss';
import SelfServiceDeviceTransactionSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/selfService/SelfServiceDeviceTransactionSimulator';
import TechnicalServiceDeviceTransactionSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/technical/TechnicalServiceDeviceTransactionSimulator';
import CustomerServiceDeviceTransactionSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/customerService/CustomerServiceDeviceTransactionSimulator';
import FullServiceDeviceTransactionSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/fullService/FullServiceDeviceTransactionSimulator';

const DeviceTransactionSimulator = (props) => {
  const { device, onBack, onSubmit, setDeviceTransactionType } = props;

  // form data
  const [selectedServiceTypeValue, setSelectedServiceTypeValue] = useState(null);
  const [serviceTypeConstants, setServiceTypeConstants] = useState({});
  const [savedServiceTypeValue, setSavedServiceTypeValue] = useState(null);

  /**
   * Create an object constant for service types that can be used for various validation logic
   *
   * @param {array} serviceTypeData
   */
  const createServiceTypeConstants = (serviceTypeData) => {
    let newObject = {};
    for (const serviceType of serviceTypeData) {
      newObject[serviceType.rawValue] = serviceType.rawValue;
    }
    setServiceTypeConstants(newObject);
  };

  /**
   * fetch the list of turn serviceType values on initialization
   */
  const { data: serviceTypes, isLoading } = useQuery(
    ['getTurnServiceTypes'],
    async () => {
      const { data } = await getTurnServiceTypes();
      createServiceTypeConstants(data.serviceTypes);
      return data.serviceTypes;
    },
    {
      onError: (error) => {
        toaster(`Error fetching service types: ${error?.response?.data?.error}`);
      },
    },
  );

  /**
   * fetch the list of team members belonging to a business
   */
  const { data: teamMembers, isTeamMembersLoading } = useQuery(
    ['getTeamMembersPerBusiness'],
    async () => {
      const { data } = await getAllTeamMembersForBusiness(device.businessId);
      return data.teamMembers;
    },
    {
      onError: (error) => {
        toaster(`Error fetching team members per business: ${error?.response?.data?.error}`);
      },
    },
  );

  /**
   * fetch the list of customers belonging to a business
   */
  const { data: customers, isCustomersLoading } = useQuery(
    ['getAllCustomersForBusiness'],
    async () => {
      const { data } = await getAllCustomersForBusiness(device.businessId);
      return data.customers;
    },
    {
      onError: (error) => {
        toaster(`Error fetching customers per business: ${error?.response?.data?.error}`);
      },
    },
  );

  /**
   * when clicking next on the service type selection, show the individual components dedicated to those service types
   */
  const handleNextOnServiceTypeSelection = () => {
    setSavedServiceTypeValue(selectedServiceTypeValue);
    setDeviceTransactionType(selectedServiceTypeValue);
  };

  /**
   * set the selected service type value in state
   *
   * @param {object} serviceTypeData
   */
  const handleServiceTypeSelection = (serviceTypeData) => {
    setSelectedServiceTypeValue(serviceTypeData.rawValue);
  };

  /**
   * render the service type selection required for initializing a device
   */
  const renderServiceTypeSelection = () => {
    return (
      <>
        <header className={styles.header}>
          <h4>Choose your service type</h4>
          <p>
            Choose the type of turn service you want to simulate. Here, you can choose between turns
            that require payment from a customer or turns that are run for customers via the various
            apps on the Cents platform.
          </p>
        </header>
        <main className={styles.main}>
          <section className={styles.section}>
            <p>Choose the service type</p>
            <Autocomplete
              disablePortal
              id="autocomplete-choose-service-type"
              getOptionLabel={(option) => option.rawValue ?? option}
              options={serviceTypes}
              sx={{ width: '300px' }}
              onChange={(event, value) => {
                handleServiceTypeSelection(value);
              }}
              renderInput={(params) => <TextField {...params} label="Service Types" />}
              value={selectedServiceTypeValue}
              isOptionEqualToValue={(option, value) => option.rawValue === value}
            />
          </section>
        </main>
        <footer className={styles.footer}>
          <p className={styles.backButton} onClick={onBack}>
            Back
          </p>
          <button
            className={classNames(styles.backButton, {
              disabled: !selectedServiceTypeValue,
            })}
            disabled={!selectedServiceTypeValue}
            onClick={handleNextOnServiceTypeSelection}
          >
            Next
          </button>
        </footer>
      </>
    );
  };

  /**
   * render the full service simulation wizard
   */
  const renderFullServiceDeviceTransactionSimulator = () => {
    return (
      <FullServiceDeviceTransactionSimulator
        device={device}
        onBack={() => {
          setSelectedServiceTypeValue(null);
          setSavedServiceTypeValue(null);
          setDeviceTransactionType(null);
        }}
        onSubmit={onSubmit}
        teamMembers={teamMembers}
      />
    );
  }

  /**
   * render the customer service simulation wizard
   */
  const renderCustomerServiceDeviceTransactionSimulator = () => {
    return (
      <CustomerServiceDeviceTransactionSimulator
        device={device}
        onBack={() => {
          setSelectedServiceTypeValue(null);
          setSavedServiceTypeValue(null);
          setDeviceTransactionType(null);
        }}
        onSubmit={onSubmit}
        teamMembers={teamMembers}
        customers={customers}
      />
    );
  }

  /**
   * render the technical service simulation wizard
   */
  const renderTechnicalServiceDeviceTransactionSimulator = () => {
    return (
      <TechnicalServiceDeviceTransactionSimulator
        device={device}
        onBack={() => {
          setSelectedServiceTypeValue(null);
          setSavedServiceTypeValue(null);
          setDeviceTransactionType(null);
        }}
        onSubmit={onSubmit}
        teamMembers={teamMembers}
      />
    )
  }

  /**
   * render the self-service device transaction simulator when selected
   */
  const renderSelfServiceDeviceTransactionSimulator = () => {
    return (
      <SelfServiceDeviceTransactionSimulator
        device={device}
        onBack={() => {
          setSelectedServiceTypeValue(null);
          setSavedServiceTypeValue(null);
          setDeviceTransactionType(null);
        }}
        onSubmit={onSubmit}
        customers={customers}
      />
    );
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderTransactionWizardOptions = () => {
    switch (savedServiceTypeValue) {
      case serviceTypeConstants.SELF_SERVICE:
        return renderSelfServiceDeviceTransactionSimulator();
      case serviceTypeConstants.TECHNICAL_SERVICE:
        return renderTechnicalServiceDeviceTransactionSimulator();
      case serviceTypeConstants.CUSTOMER_SERVICE:
        return renderCustomerServiceDeviceTransactionSimulator();
      case serviceTypeConstants.FULL_SERVICE:
        return renderFullServiceDeviceTransactionSimulator();
      default:
        return renderServiceTypeSelection();
    }
  };

  return <>{(isLoading || isTeamMembersLoading || isCustomersLoading) ? <CentsLoader /> : renderTransactionWizardOptions()}</>;
};

export default DeviceTransactionSimulator;
